import { isEmpty, isEqual } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { HouseholdMember, InvalidPlanMessageKey } from 'Types/entities';
import { Election, ElectionMember, JVPUser } from 'Utils/apiTypes';
import logger from 'Utils/logger';

import { BEST_FIT_SCORE, FAIR_FIT_SCORE, GREAT_FIT_SCORE, labelOptions, PAGE_SLUGS } from './constants';

export const getCookie = (key: string): string => {
  const cookieKeyValue = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${key}`)) || '';
  const [, value] = cookieKeyValue.split('=');
  return value;
};

export const setCookie = (
  key: string,
  value: string | boolean,
  domain?: string,
  path?: string,
  expirationInMinutes?: number,
  secure?: boolean,
) => {
  let cookieContents = `${key}=${value}`;
  if (domain) {
    cookieContents += `;domain=${domain}`;
  }
  if (path) {
    cookieContents += `;path=${path}`;
  }
  if (expirationInMinutes) {
    const exp = new Date();
    const expireTime = exp.getTime() + expirationInMinutes * 60 * 1000;
    exp.setTime(expireTime);
    cookieContents += `;expires=${exp.toUTCString()}`;
  }
  if (secure === true) {
    cookieContents += `;secure`;
  }
  document.cookie = cookieContents;
};

export const defaultCookieDomain = () => {
  if (process.env.NODE_ENV === 'development') {
    return undefined;
  }

  if (window.location.hostname.includes('jellydevs.com')) {
    return 'jellydevs.com';
  }

  return 'myalex.com';
};

export const deleteCookie = (key: string, domain?: string, path?: string) => {
  let cookieContents = `${key}=`;
  if (domain) {
    cookieContents += `;domain=${domain}`;
  }
  if (path) {
    cookieContents += `;path=${path}`;
  }

  cookieContents += '; expires = Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = cookieContents;
};

const sourcePrefixForJvp = 'guest__';
export const stripSourceComponent = (jvpId: string) => jvpId.replace(sourcePrefixForJvp, '');

// NOTE: AAPI requires identifiers that are exactly 32 chars so we have to strip
//   hyphens from our external id component.
//  We'll maintain this format for all external ids for consistency.
export const makeNewCommExternalId = () => `${sourcePrefixForJvp}${uuidv4().replace(/-/g, '')}`;

export const parseJvpUserId = (user: JVPUser) =>
  user.auth0_uuid || `${user.external_source}__${user.external_id}`;

const urlSegmentIsPageSlug = (segment: string): boolean => {
  return PAGE_SLUGS.includes(segment);
};

/**
 * Employer slug is always that first part of the URL before any page slugs
 * It is used for analytics events and bootstrapping
 * ie: if we have a customer with a segment like "lyondellbasell/edison" we expect "lyondellbasell"
 */
export const getEmployerSlugFromLocation = (location: Location): string => {
  const nonEmptyPathParts = location.pathname.split('/').filter(Boolean);
  return nonEmptyPathParts[0];
};

/**
 * Segment slug for cases where we have like lyondellbasell/edison
 * We want the last part (domain.tld/(employer)(/segment)?(/page)? of the URL path excluding page slug segments
 * If the url excluding page slug is only 1 part, we do not have a segment slug
 */
export const getSegmentSlugFromLocation = (location: Location): string => {
  const nonEmptyPathParts = location.pathname.split('/').filter(Boolean);

  // filter out path parts that resemble a page slug.
  const slugParts = nonEmptyPathParts.filter((seg) => {
    return !urlSegmentIsPageSlug(seg);
  });

  return slugParts.length > 1 ? slugParts[slugParts.length - 1] : '';
};

export const getFullAlexUrl = (location: Location, segmentSlug: string | undefined): string =>
  location.href + (segmentSlug || '');

export const formatPathForInsights = (path, fixedPath) => {
  if (path === fixedPath) {
    return 'landing';
  }

  const parts = path && path?.split('/');

  // Filter out any empty parts resulting from leading/trailing slashes
  const filteredParts = parts && parts?.filter((part) => part !== '');

  // If there's only one part, return it as is
  if (filteredParts?.length <= 1) {
    return filteredParts[0] || '';
  }

  // Join all parts except the first one
  return filteredParts?.slice(1).join('/');
};

export const isDevelopment = () => {
  return process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test';
};

export const moveSelectedPlanToTop = (availablePlans, result) => {
  const preselectedPlanId = result?.election?.plan_details?.jv_medical_plan_id;

  // Reorder availablePlans array with the preselected plan first
  const reorderedPlans = [...availablePlans];

  // Find the index of the preselected plan
  const preselectedIndex = reorderedPlans?.findIndex((plan) => plan?.plan?.external_id === preselectedPlanId);

  if (preselectedIndex !== -1) {
    // Remove the preselected plan from its current position
    const [preselectedPlan] = reorderedPlans?.splice(preselectedIndex, 1) || [];

    // Insert the preselected plan at the beginning of the array
    reorderedPlans?.unshift(preselectedPlan);
  }

  return reorderedPlans;
};

export const getInvalidPlanMessageKey = (
  tierCode: string[],
  hasElection: boolean,
  householdMembers: HouseholdMember[],
  electionMembersEnabled: boolean,
  matchedPlan?: Election,
): InvalidPlanMessageKey | null => {
  // null represents the case where we don't have to invalidate the user's existing selection and show them a message

  if (!hasElection) return null;

  if (!matchedPlan) return InvalidPlanMessageKey.NoLongerEligible;

  if (electionMembersEnabled) {
    // If jvpUser ever becomes the source of truth in the FE instead of household.members
    // for user & dependent data, it would enable us to compare apples-to-apples
    // re: external_id and not have to strip the "guest__" part from it in the FE via stripSourceComponent()

    const dependentAndSigOtherIds = householdMembers
      .filter((m) => !m.policyholder)
      .map((m) => stripSourceComponent(m.external_id));

    const electionDependentAndSigOtherIds = (matchedPlan.election_members || [])
      .filter((m: ElectionMember) => m.relationship !== 'self' && m.external_id !== null)
      .map((m: ElectionMember) => m.external_id as string);

    if (
      dependentAndSigOtherIds.length !== electionDependentAndSigOtherIds.length ||
      !isEqual(new Set(dependentAndSigOtherIds), new Set(electionDependentAndSigOtherIds))
    ) {
      return InvalidPlanMessageKey.HouseholdMembersChanged;
    }
  }

  if (isEmpty(tierCode)) return null;

  if (parseInt(matchedPlan.tier_code || '0', 10) !== parseInt(tierCode[0], 10)) {
    return InvalidPlanMessageKey.HouseholdMembersChanged;
  }

  return null;
};

export const fetchShowVideoStatus = (insuranceType: string): string => {
  const insuranceTypeStatusMapping = {
    accident: 'show_accident_video',
    add: 'show_add_video',
    commuter: 'show_commuter_video',
    critical: 'show_critical_illness_video',
    dental: 'show_dental_video',
    employee_assistance_program: 'show_eap_video',
    financial_wellness: 'show_financial_wellness_video',
    fsa: 'show_fsa_video',
    fsa_dependent: 'show_dcfsa_video',
    fsa_medical: 'show_fsa_video',
    hospital: 'show_hospital_indemnity_video',
    hra: 'show_hra_video',
    hsa: 'show_hsa_video',
    legal: 'show_legal_video',
    life: 'show_group_life_video',
    lpfsa: 'show_lpfsa_video',
    ltd: 'show_ltd_video',
    retirement: 'show_retirement_video',
    '401k': 'show_retirement_video',
    '403b': 'show_retirement_video',
    std: 'show_std_video',
    teledoc: 'show_telemedicine_video',
    vision: 'show_vision_video',
    wellness: 'show_wellness_video',
  };

  return insuranceTypeStatusMapping[insuranceType] || 'other';
};

export const sortPlansAlphabetically = (availablePlans) =>
  [...availablePlans]?.sort((a, b) =>
    a?.plan?.plan_marketing_name?.localeCompare(b?.plan?.plan_marketing_name),
  );

export const handleBcNavigate = (
  jvp,
  isDemoMode,
  planYear,
  validatedSegmentSlug,
  bcUrlHash,
  GO_GUEST_ID,
  isAuthenticated,
  getIdTokenClaims,
  getBCPath,
) => {
  if (jvp.sessionId) {
    setCookie('session_id', jvp.sessionId, defaultCookieDomain(), '/', 2);
  }

  // Create a "guest user ID" cookie for guest users
  if (!isAuthenticated && jvp.userId) {
    // BC is expecting a 32 char identifier here
    const goGuestIdForCookie = stripSourceComponent(jvp.userId);
    setCookie(GO_GUEST_ID, goGuestIdForCookie, defaultCookieDomain(), '/');
  }

  // Create a JWT cookie for AAPI/BC for logged in users
  if (isAuthenticated) {
    getIdTokenClaims()
      .then((token) => {
        const idToken = token?.__raw;
        if (idToken) {
          setCookie('jv_jwt', idToken, defaultCookieDomain(), '/', 1, true);
        }
      })
      .catch((tokenError) => logger.error(tokenError));
    const authCookieName = `auth0.${window._env_.REACT_APP_AUTH0_CLIENT_ID}.is.authenticated`;
    const auth0CookieValue = getCookie(authCookieName);
    setCookie(authCookieName, auth0CookieValue, defaultCookieDomain(), '/', 1440);
  }

  const bcPath = getBCPath(isDemoMode, planYear?.toString() || '', validatedSegmentSlug, bcUrlHash);

  window.onbeforeunload = null;
  window.location.assign(bcPath);
};

export const getPlanFitField = (score: number) => {
  // will get the field name based on plan score here and retrive its contentful text from result page
  let fieldName = labelOptions?.poor;

  if (score >= BEST_FIT_SCORE) {
    // BEST
    fieldName = labelOptions?.best;
  } else if (score >= GREAT_FIT_SCORE) {
    // GREAT
    fieldName = labelOptions?.great;
  } else if (score >= FAIR_FIT_SCORE) {
    // FAIR
    fieldName = labelOptions?.fair;
  } else {
    // POOR
    fieldName = labelOptions?.poor;
  }

  return fieldName;
};

// When accident_no_premiums is true, hide the premium from UI for accident plan
export const hideAccidentPremiums = (plan, builderGoSettings) =>
  plan?.plan_type === 'accident' && builderGoSettings?.accident_no_premiums;

export const normalizePlanName = (input?: string) => {
  if (!input || typeof input !== 'string') return '';

  const trimmed = input?.trim();
  const words = trimmed?.split(/\s+/);
  const lastWord = words[words.length - 1]?.toLowerCase();

  return lastWord === 'plan' ? trimmed : `${trimmed} plan`;
};
